import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, Button, VisuallyHidden } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import addressPropType from './AddressConstants';
import { UpdateShippingAddressModal } from './UpdateShippingAddressModal';

const ButtonText = () => {
  const [shippingBillingCard] = useTranslations(['shippingBillingCard']);
  return (
    <>
      {shippingBillingCard.change}
      <VisuallyHidden>{shippingBillingCard.shippingAddress}</VisuallyHidden>
    </>
  );
};

/**
 * This component provides a button to invoke a modal that contains the address
 * form that allows a customer to edit the shipping address of their order.
 * @param address the current address of the order
 * @param linkStyle how to display the button (button/link style), defaults to link style
 * @param headerText display text of the modals header
 * @returns {JSX.Element}
 * @constructor
 */
export const UpdateShippingAddressButton = ({
  address,
  buttonStyle = 'link',
}) => {
  const [isModalOpen, setModalIsOpen] = useState(false);

  const closeModal = () => {
    setModalIsOpen(false);
  };
  const openModal = () => {
    setModalIsOpen(true);
  };

  return (
    <>
      {buttonStyle === 'link' ? (
        <Link
          render={p => (
            <Button skin="link" onClick={openModal} className={p.className}>
              {p.children}
            </Button>
          )}
        >
          <ButtonText />
        </Link>
      ) : (
        <Button onClick={openModal}>
          <ButtonText />
        </Button>
      )}
      {isModalOpen ? (
        <UpdateShippingAddressModal
          address={address}
          isModalInitiallyOpen={isModalOpen}
          closeModal={closeModal}
        />
      ) : null}
    </>
  );
};

/**
 * For a given order, determine if *any* item has a post order action override
 * to disable shipping address edits
 * @param order
 * @returns {boolean} true if any item contains a shipping address edit post order action override
 */
export const anyItemContainsShippingAddressEditRestrictions = order => {
  for (const orderItem of order.lineItems) {
    if (
      orderItem?.customOrderData?.order?.postOrderActionOverrides
        ?.shippingAddressEdit?.disabled === true
    )
      return true;
  }
  return false;
};

UpdateShippingAddressButton.propTypes = {
  address: PropTypes.exact(addressPropType),
  buttonStyle: PropTypes.string,
};
